import { useCallback, useEffect, useState } from "react";
import { Check, FileDown, Loader2, Pause, Play, RotateCw, Square, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, download } from "../../lib/api";
import { usePoll } from "../../hooks/usePoll";
import { Button } from "../ui/button";
import { NoManuscript } from "./NoManuscript";
import { useModels, ModelSelect, ChapterPicker } from "./shared";
import { JobView as ProofJobView } from "./ProofreadTab";
import { JobView as RewriteJobView } from "./RewriteTab";
import { ContinueCard, useGoTab } from "./ContinueCard";
import { PolishPresets } from "./PolishPresets";
import { SeriesPolishPanel } from "../series/SeriesPolishPanel";

const PASSES = [["developmental", "Structure edit"], ["line", "Line edit"], ["proofread", "Proofread"]];

const Stepper = ({ p }) => (
  <ol className="grid sm:grid-cols-3 gap-3" data-testid="pipeline-stepper">
    {PASSES.map(([k, label], i) => {
      const step = p.steps[i];
      const state = p.status === "done" || i < p.idx ? "done" : i === p.idx && p.status !== "cancelled" ? "now" : "later";
      return (
        <li key={k} data-testid={`pipeline-pass-${k}`} className={`rounded-lg border p-3 ${state === "done" ? "border-emerald-500/40 bg-emerald-900/10" : state === "now" ? "border-amber-500/50 bg-amber-500/10" : "border-slate-800"}`}>
          <p className="flex items-center gap-2 text-sm font-medium">{state === "done" ? <Check size={14} className="text-emerald-300" /> : state === "now" ? (p.status === "waiting" ? <Pause size={14} className="text-amber-300" /> : <Loader2 size={14} className="animate-spin text-amber-300" />) : <span className="font-mono text-[11px] text-slate-500">{i + 1}</span>}{label}</p>
          {step && <p className="text-[11px] text-slate-400 mt-1 font-mono">{step.fixes_applied} fix(es) · {step.notes} note(s){step.version_number ? ` · v${step.version_number}` : ""}{step.retry_rewrite_job_id ? " · retried leftovers" : ""}</p>}
        </li>
      );
    })}
  </ol>
);

const Start = ({ book, version, onStarted }) => {
  const models = useModels();
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => version.chapters.map((c) => c.id));
  useEffect(() => { if (models && !model) setModel(models.default); }, [models, model]);
  const start = async () => {
    if (!selected.length) return toast.error("Tick at least one chapter");
    setBusy(true);
    const all = selected.length === version.chapters.length;
    try { onStarted((await api.post(`/books/${book.id}/pipeline`, { model, chapter_ids: all ? [] : selected })).data); toast.success("One-click polish started"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="panel p-6 space-y-4 max-w-2xl" data-testid="pipeline-start">
      <p className="eyebrow">One-click polish</p>
      <p className="text-sm text-slate-300">Runs a <b>structure edit</b>, then a <b>line edit</b>, then a <b>proofread</b> on the chapters you tick below. After each one, word fixes are saved and accepted notes are rewritten. At the end, the final version is approved for your editions.</p>
      <ul className="text-xs text-slate-400 space-y-1 list-disc pl-5">
        <li>It stops for you to check each set of findings (important ones are pre-ticked).</li>
        <li>It stops for you to check what must stay unchanged before any rewrite.</li>
        <li>Every step is saved as a version, so you can go back at any time.</li>
      </ul>
      <PolishPresets book={book} version={version} model={model} selected={selected} onPick={(x) => { if (x.model) setModel(x.model); setSelected(x.selected); }} />
      <ModelSelect value={model} onChange={setModel} models={models} testId="pipeline-model-select" />
      <ChapterPicker chapters={version.chapters} selected={selected} setSelected={setSelected} testPrefix="pipeline" />
      <p className="text-xs text-slate-500" data-testid="pipeline-chapter-count">{selected.length === version.chapters.length ? "Whole book" : `${selected.length} of ${version.chapters.length} chapters`} will be polished.</p>
      <Button onClick={start} disabled={busy} className="bg-amber-600 hover:bg-amber-500" data-testid="pipeline-start-button"><Wand2 size={14} className="mr-1" />Start one-click polish</Button>
    </div>
  );
};

export default function PipelineTab(props) {
  return (
    <div className="space-y-5">
      <PipelineBody {...props} />
      {props.book.series_id && props.version && <div className="panel p-5 max-w-2xl" data-testid="pipeline-series-polish"><p className="text-sm text-slate-300">This book is part of <b>{props.book.series_name}</b>.</p><SeriesPolishPanel sid={props.book.series_id} currentBookId={props.book.id} /></div>}
    </div>
  );
}

function PipelineBody({ book, version, reload }) {
  const go = useGoTab();
  const [p, setP] = useState(undefined);
  const [fresh, setFresh] = useState(false);
  const load = useCallback(() => api.get(`/books/${book.id}/pipeline`).then((r) => setP((old) => { if (old && old.status !== r.data?.status) reload(); return r.data; })).catch(() => setP(null)), [book.id, reload]);
  useEffect(() => { load(); }, [load]);
  usePoll(load, p && ["running", "waiting"].includes(p.status), 5000);
  if (!version) return <NoManuscript />;
  if (p === undefined) return <div className="panel h-40 animate-pulse" data-testid="pipeline-loading" />;
  if (!p || fresh) return <Start book={book} version={version} onStarted={(x) => { setFresh(false); setP(x); }} />;
  const act = async (a) => { try { const { data } = await api.post(`/pipelines/${p.id}/${a}`); if (a === "cancel") load(); else setP(data); } catch (e) { toast.error(errMsg(e)); } };
  const active = ["running", "waiting"].includes(p.status);
  return (
    <div className="space-y-5" data-testid="pipeline-tab">
      <div className="panel p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="eyebrow">One-click polish · {p.status === "done" ? "finished" : p.status === "cancelled" ? "stopped" : p.status === "waiting" ? "waiting for you" : "working"}</p>
          <div className="flex gap-2">
            {p.retry && <Button size="sm" onClick={() => act("retry")} className="bg-amber-600 hover:bg-amber-500" data-testid="pipeline-retry"><RotateCw size={13} className="mr-1" />{p.retry === "restart_pass" ? "Retry" : "Resume"}</Button>}
            {active && <Button size="sm" variant="outline" className="border-slate-700" onClick={() => window.confirm("Stop the one-click polish? Saved versions stay.") && act("cancel")} data-testid="pipeline-cancel"><Square size={13} className="mr-1" />Stop</Button>}
            {!active && <Button size="sm" variant="outline" className="border-slate-700" onClick={() => setFresh(true)} data-testid="pipeline-new"><Play size={13} className="mr-1" />Run again</Button>}
          </div>
        </div>
        <Stepper p={p} />
        {p.message && <p className="text-sm text-slate-300" data-testid="pipeline-message">{p.message}</p>}
        {p.steps.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 pt-3" data-testid="pipeline-report">
            <span className="text-xs text-slate-400 inline-flex items-center gap-1"><FileDown size={13} />Before-and-after report</span>
            {[["docx", "Word"], ["pdf", "PDF"]].map(([f, l]) => (
              <Button key={f} size="sm" variant="outline" className="h-7 border-slate-700" data-testid={`pipeline-report-${f}`}
                onClick={() => download(`/pipelines/${p.id}/report?format=${f}`, `polish_report.${f}`).catch((e) => toast.error(errMsg(e)))}>{l}</Button>
            ))}
          </div>
        )}
      </div>
      {p.stage === "review_findings" && p.status === "waiting" && <ProofJobView key={p.proof_job_id} jobId={p.proof_job_id} bookId={book.id} onApplied={reload} onContinue={() => act("continue")} />}
      {["check_manifest", "rewriting", "preparing_rewrite"].includes(p.stage) && active && <RewriteJobView key={p.rewrite_job_id} jobId={p.rewrite_job_id} approvedId={book.approved_version_id} onApplied={reload} hideApply />}
      {p.status === "done" && <>
        <ContinueCard testId="pipeline-done-card" title={`v${p.final_version_number} is approved`} text="Build your editions from the polished version." actions={[["ebook", "Ebook"], ["paperback", "Paperback"]].map(([t, l]) => ({ label: `Build ${l.toLowerCase()}`, onClick: () => go(t), testId: `pipeline-done-${t}` }))} />
        {p.steps.flatMap((s) => [[s.rewrite_job_id, !!s.retry_rewrite_job_id], [s.retry_rewrite_job_id, false]]).filter(([id]) => id).map(([id, retried]) => <RewriteJobView key={id} jobId={id} approvedId={book.approved_version_id} hideApply noLeftovers={retried} />)}
      </>}
    </div>
  );
}
